/**
 * GrantScope — how far a grant reaches, named without naming the domain.
 *
 * Both consuming products carry structurally identical unions that differ only
 * in their domain noun: tickets `{all-events, orgId} | {events, eventIds}`,
 * momo-starter `{all-locations, orgId} | {locations, locationIds}`. Neither
 * noun is a fact the slice can know, and the tenant id both `all-` variants
 * restate is already carried by the document path — so the shared shape is
 * `all | scoped(scopeIds)` and nothing else.
 *
 * No `orgId`, ever. A scope that restated its tenant would give every reader
 * two places to learn one fact and one of them the chance to be wrong; the path
 * is the authority. Tickets' own `MemberScopeSchema` already dropped it.
 */
import { z } from "zod";

export const GrantScopeSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("all") }),
	z.object({
		kind: z.literal("scoped"),
		scopeIds: z.array(z.string().min(1)).min(1),
	}),
]);

export type GrantScope = z.infer<typeof GrantScopeSchema>;

/** The scope ids this grant reaches, or `null` for "every scope under the tenant". */
export function scopeIdsOf(scope: GrantScope): readonly string[] | null {
	return scope.kind === "all" ? null : scope.scopeIds;
}
